import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, Marker } from 'react-leaflet';
import { FiChevronDown, FiChevronUp, FiChevronsRight, FiLayers, FiList, FiSearch } from 'react-icons/fi';
import L from 'leaflet';
import * as turf from '@turf/turf';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import BlockEditor from './BlockEditor';
import BlockStatusDetailsTable from './BlockStatusDetailsTable';
import LayerTreeSelector from './LayerTreeSelector';
import {
  blockLayerNumber,
  getStoredLayerPanelScrollMaxPx,
  setStoredLayerPanelScrollMaxPx,
} from '../utils/layerUtils';
import './MapView.css';

const MAP_SIDE_PANELS_VISIBLE_KEY = 'mapLegendLayersVisible';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function MapController({ bounds, projectId }) {
  const map = useMap();
  const hasFittedRef = useRef(false);
  const lastProjectIdRef = useRef(null);
  
  useEffect(() => {
    // Reset when project changes
    if (projectId !== lastProjectIdRef.current) {
      hasFittedRef.current = false;
      lastProjectIdRef.current = projectId;
    }
    
    // Only fit bounds once per project
    if (bounds && bounds.length > 0 && !hasFittedRef.current) {
      map.fitBounds(bounds, { padding: [50, 50] });
      hasFittedRef.current = true;
    }
  }, [bounds, map, projectId]);

  return null;
}

/** User-triggered zoom from layer tree (distinct from initial project fit). */
function MapManualFitController({ fitRequest }) {
  const map = useMap();
  useEffect(() => {
    if (!fitRequest?.bounds) return;
    try {
      map.invalidateSize();
      map.fitBounds(fitRequest.bounds, {
        padding: [34, 34],
        maxZoom: 17,
        animate: true,
      });
    } catch (err) {
      console.warn('Map fitBounds failed:', err);
    }
  }, [fitRequest, map]);
  return null;
}

function MapView({
  project,
  visibleBlocks: visibleBlocksProp,
  onBlockStatusUpdate,
  selectedLayerIds,
  onLayerSelectionChange,
  showBlockLabels = true
}) {
  const { canEdit } = useAuth();
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [panelHeight, setPanelHeight] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [isTableVisible, setIsTableVisible] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const blockLayersRef = useRef({});
  const panelRef = useRef(null);
  const layerPanelDragRef = useRef({ startY: 0, startMax: 260 });
  const layerPanelHeightCommitted = useRef(260);

  const [fitRequest, setFitRequest] = useState(null);
  const [layerPanelScrollMax, setLayerPanelScrollMax] = useState(() =>
    getStoredLayerPanelScrollMaxPx(260)
  );
  const [isResizingLayerPanel, setIsResizingLayerPanel] = useState(false);

  const [sidePanelsOpen, setSidePanelsOpen] = useState(() => {
    try {
      const v = localStorage.getItem(MAP_SIDE_PANELS_VISIBLE_KEY);
      if (v === null) return false;
      return v === '1' || v === 'true';
    } catch {
      return false;
    }
  });

  const requestMapFitBounds = useCallback((bounds) => {
    if (!bounds) {
      toast.info('Nothing to zoom to — no block outlines for this selection.');
      return;
    }
    setFitRequest({ key: Date.now(), bounds });
  }, []);

  const allBlocksForNumber = project?.blocks || [];
  const blocks = visibleBlocksProp ?? allBlocksForNumber;
  /** Skip invalid geometries — leaflet/react-leaflet can throw on null/invalid GeoJSON */
  const blocksForMap = useMemo(
    () => (blocks || []).filter((b) => b?.block_geojson?.geometry),
    [blocks]
  );

  // Calculate bounds from polygon using turf bbox (handles Polygon/MultiPolygon/Feature reliably)
  const bounds = useMemo(() => {
    if (!project?.polygon_geojson) return null;
    try {
      let gj = project.polygon_geojson;
      if (typeof gj === 'string') gj = JSON.parse(gj);
      const box = turf.bbox(gj);
      const [minLng, minLat, maxLng, maxLat] = box;
      if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
      if (Math.abs(maxLat - minLat) < 1e-14 || Math.abs(maxLng - minLng) < 1e-14) return null;
      return [
        [minLat, minLng],
        [maxLat, maxLng]
      ];
    } catch {
      return null;
    }
  }, [project?.polygon_geojson]);

  const mapCenter =
    bounds &&
    Number.isFinite((bounds[0][0] + bounds[1][0]) / 2) &&
    Number.isFinite((bounds[0][1] + bounds[1][1]) / 2)
      ? [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2]
      : [23.886, 44.957];

  // Calculate block areas and centroids for labels - must be before early return
  const blockLabels = useMemo(() => {
    if (!project || !blocksForMap.length || !allBlocksForNumber.length) {
      return [];
    }
    return blocksForMap.map((block) => {
      try {
        const blockGeoJSON = block.block_geojson;
        if (!blockGeoJSON || !blockGeoJSON.geometry) {
          return null;
        }
        const areaInSquareMeters = turf.area(blockGeoJSON);
        const areaInSquareKm = areaInSquareMeters / 1000000;
        const centroid = turf.centroid(blockGeoJSON);
        const [lng, lat] = centroid.geometry.coordinates;
        const blockNumber = blockLayerNumber(block, allBlocksForNumber);
        return {
          blockId: block.id,
          blockNumber,
          area: areaInSquareKm,
          position: [lat, lng]
        };
      } catch (error) {
        console.error(`Error calculating area for block ${block.id}:`, error);
        return null;
      }
    }).filter(Boolean);
  }, [project, blocksForMap, allBlocksForNumber]);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const mapView = panelRef.current?.closest('.map-view');
      if (!mapView) return;
      
      const mapRect = mapView.getBoundingClientRect();
      const newHeight = mapRect.bottom - e.clientY;
      
      // Limit height between 100px and 80% of viewport
      const minHeight = 100;
      const maxHeight = window.innerHeight * 0.8;
      
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setPanelHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!isResizingLayerPanel) return;
      const delta = e.clientY - layerPanelDragRef.current.startY;
      const next = Math.round(
        Math.min(640, Math.max(100, layerPanelDragRef.current.startMax + delta))
      );
      layerPanelHeightCommitted.current = next;
      setLayerPanelScrollMax(next);
    };
    const handleUp = () => {
      if (!isResizingLayerPanel) return;
      setIsResizingLayerPanel(false);
      setStoredLayerPanelScrollMaxPx(layerPanelHeightCommitted.current);
    };
    if (isResizingLayerPanel) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isResizingLayerPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(MAP_SIDE_PANELS_VISIBLE_KEY, sidePanelsOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidePanelsOpen]);

  const handleLayerPanelResizeMouseDown = (e) => {
    e.preventDefault();
    layerPanelDragRef.current = {
      startY: e.clientY,
      startMax: layerPanelScrollMax,
    };
    layerPanelHeightCommitted.current = layerPanelScrollMax;
    setIsResizingLayerPanel(true);
  };

  if (!project || !project.polygon_geojson) {
    return <div className="map-placeholder">Loading map...</div>;
  }

  let polygonGeojson = project.polygon_geojson;
  if (typeof polygonGeojson === 'string') {
    try {
      polygonGeojson = JSON.parse(polygonGeojson);
    } catch {
      return <div className="map-placeholder">Invalid project outline (polygon).</div>;
    }
  }

  const getBlockColor = (block) => {
    // Color based on the highest completed status (Processing > Acquisition > GCPs)
    if (block.processing_status === 1) {
      return '#1b5e20'; // Very dark green - Processing Complete
    }
    if (block.acquisition_status === 1) {
      return '#2196f3'; // Blue - Acquisition Complete
    }
    if (block.gcp_status === 1) {
      return '#ffc107'; // Yellow - GCPs Complete
    }
    return '#f44336'; // Red - Not Started
  };

  const handleBlockClick = (block, layer) => {
    if (canEdit()) {
      setSelectedBlock(block);
      setShowEditor(true);
    }
  };

  const handleBlockMouseOver = (block, layer, e) => {
    layer.setStyle({
      weight: 3,
      opacity: 0.8
    });

    // Calculate completion percentage
    const statuses = [
      block.gcp_status,
      block.acquisition_status,
      block.processing_status
    ];
    const completedCount = statuses.filter(s => s === 1).length;
    const completionPercent = Math.round((completedCount / 3) * 100);

    // Get block number within KML layer
    const blockNumber = blockLayerNumber(block, project.blocks || []);

    setTooltipContent({
      blockNumber,
      completion: completionPercent,
      gcp: block.gcp_status === 1,
      acquisition: block.acquisition_status === 1,
      processing: block.processing_status === 1
    });

    if (e.originalEvent) {
      setTooltipPosition({
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY
      });
    }
  };

  const handleBlockMouseOut = (block, layer) => {
    layer.setStyle({
      weight: 2,
      opacity: 0.6
    });
    setTooltipContent(null);
  };

  const polygonStyle = {
    color: '#2196f3',
    weight: 2,
    opacity: 0.7,
    fillOpacity: 0.1
  };

  const getBlockStyle = (block) => {
    const color = getBlockColor(block);
    
    // Different border colors for better visibility based on highest completed status
    let borderColor = '#c62828'; // Default: red for not started
    if (block.processing_status === 1) {
      borderColor = '#0d4f0d'; // Dark green border for Processing Complete
    } else if (block.acquisition_status === 1) {
      borderColor = '#1976d2'; // Blue border for Acquisition Complete
    } else if (block.gcp_status === 1) {
      borderColor = '#f57f17'; // Dark yellow/orange border for GCPs Complete
    }
    
    // Determine opacity based on status
    let fillOpacity = 0.4; // Default for not started
    if (block.processing_status === 1) {
      fillOpacity = 0.7; // Highest opacity for Processing Complete
    } else if (block.acquisition_status === 1) {
      fillOpacity = 0.5; // Medium opacity for Acquisition Complete
    } else if (block.gcp_status === 1) {
      fillOpacity = 0.35; // Lower opacity for GCPs Complete
    }
    
    return {
      color: borderColor, // Distinct border colors
      weight: block.gcp_status === 0 && block.acquisition_status === 0 && block.processing_status === 0 ? 2 : 2.5, // Slightly thicker for colored blocks
      opacity: 0.9,
      fillColor: color,
      fillOpacity: fillOpacity
    };
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setSelectedBlock(null);
  };

  const handleStatusUpdate = () => {
    onBlockStatusUpdate();
    handleEditorClose();
  };

  return (
    <div className="map-view">
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, Maxar, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        
        {bounds && <MapController bounds={bounds} projectId={project.id} />}
        <MapManualFitController fitRequest={fitRequest} />

        {/* Main polygon */}
        <GeoJSON
          data={polygonGeojson}
          style={polygonStyle}
        />

        {/* Blocks */}
        {blocksForMap.map((block, index) => (
          <React.Fragment key={block.id}>
            <GeoJSON
              data={block.block_geojson}
              style={getBlockStyle(block)}
              eventHandlers={{
                click: (e) => {
                  const layer = e.target;
                  handleBlockClick(block, layer);
                },
                mouseover: (e) => {
                  const layer = e.target;
                  handleBlockMouseOver(block, layer, e);
                },
                mouseout: (e) => {
                  const layer = e.target;
                  handleBlockMouseOut(block, layer);
                }
              }}
              ref={(ref) => {
                if (ref) {
                  blockLayersRef.current[block.id] = ref;
                }
              }}
            />
            {/* Block Label */}
            {showBlockLabels && (() => {
              const label = blockLabels.find(l => l.blockId === block.id);
              if (!label) return null;
              
              const icon = L.divIcon({
                className: 'block-label',
                html: `
                  <div class="block-label-content">
                    <div class="block-label-id">#${label.blockNumber}</div>
                    <div class="block-label-area">${label.area.toFixed(2)} km²</div>
                  </div>
                `,
                iconSize: [70, 32],
                iconAnchor: [35, 16]
              });
              
              return (
                <Marker
                  key={`label-${block.id}`}
                  position={label.position}
                  icon={icon}
                />
              );
            })()}
          </React.Fragment>
        ))}
      </MapContainer>

      {showEditor && selectedBlock && (
        <BlockEditor
          block={selectedBlock}
          onClose={handleEditorClose}
          onUpdate={handleStatusUpdate}
        />
      )}

      <div className="map-side-panels-wrap">
        {sidePanelsOpen ? (
          <div className="map-left-stack">
            <div className="map-legend">
              <div className="legend-header legend-header-row">
                <span className="legend-title">Legend</span>
                <button
                  type="button"
                  className="map-panels-hide-btn"
                  onClick={() => setSidePanelsOpen(false)}
                  title="Hide legend & layers"
                  aria-label="Hide legend and layers"
                >
                  <FiChevronsRight aria-hidden />
                </button>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#1b5e20', border: '2px solid #0d4f0d' }}></span>
                <span>Processing Complete</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#2196f3', border: '2px solid #1976d2' }}></span>
                <span>Acquisition Complete</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#ffc107', border: '2px solid #f57f17' }}></span>
                <span>GCPs Complete</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#f44336', border: '2px solid #c62828' }}></span>
                <span>Not Started</span>
              </div>
              </div>
              {Boolean(project?.kml_layers?.length) && typeof onLayerSelectionChange === 'function' && (
                <div
                  className="layer-tree-panel-shell"
                  style={{ '--layer-tree-scroll-max': `${layerPanelScrollMax}px` }}
                >
                  <LayerTreeSelector
                    projectId={project.id}
                    project={project}
                    selectedLayerIds={selectedLayerIds}
                    onSelectionChange={onLayerSelectionChange}
                    onZoomToBounds={requestMapFitBounds}
                  />
                  <div
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label="Resize layers panel height"
                    className={`layer-tree-panel-resize-handle${
                      isResizingLayerPanel ? ' layer-tree-panel-resize-active' : ''
                    }`}
                    onMouseDown={handleLayerPanelResizeMouseDown}
                    title="Drag up or down to resize the layers list"
                  />
                </div>
              )}
            </div>
        ) : (
          <button
            type="button"
            className="map-side-panels-expand-tab"
            onClick={() => setSidePanelsOpen(true)}
            title="Show legend & layers"
            aria-label="Show legend and layers"
          >
            <FiLayers className="map-side-panels-expand-icon" aria-hidden />
            <span className="map-side-panels-expand-text">Legend / Layers</span>
          </button>
        )}
      </div>

      <div className="map-bottom-panel-wrapper">
        {isTableVisible && (
          <div 
            ref={panelRef}
            className="map-bottom-panel"
            style={{ 
              height: `${panelHeight}px`,
              '--panel-height': `${panelHeight}px`
            }}
          >
            <div 
              className="resize-handle"
              onMouseDown={handleMouseDown}
            >
              <div className="resize-handle-line"></div>
            </div>
            <div className="table-header-container">
              <button
                className="table-toggle-btn"
                onClick={() => setIsTableVisible(!isTableVisible)}
                title="Hide Status Details"
              >
                <FiList className="table-toggle-icon" />
                <div className="header-search-container" onClick={(e) => e.stopPropagation()}>
                  <FiSearch className="header-search-icon" />
                  <input
                    type="text"
                    className="header-search-input"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <span>Status Details ({blocks.length} blocks)</span>
                <FiChevronDown className="table-toggle-chevron" />
              </button>
            </div>
            <BlockStatusDetailsTable 
              blocks={blocks} 
              allBlocksForNumbering={project.blocks}
              onStatusUpdate={onBlockStatusUpdate}
              searchQuery={searchQuery}
            />
          </div>
        )}
        {!isTableVisible && (
          <button
            className="table-toggle-btn"
            onClick={() => setIsTableVisible(!isTableVisible)}
            title="Show Status Details"
          >
            <FiList className="table-toggle-icon" />
            <span>Status Details ({blocks.length} blocks)</span>
            <FiChevronUp className="table-toggle-chevron" />
          </button>
        )}
      </div>
    </div>
  );
}

export default MapView;

